import {createContext, memo, useCallback, use, useEffect, useMemo, useReducer, useState} from 'react'
import {Button, FormControl, Textarea} from '@primer/react'
import {TriangleDownIcon} from '@primer/octicons-react'
import {ModelPicker, type ModelOption} from '@github-ui/copilot-fix-controls/ModelPicker'
import {
  CopilotFixSplitButton,
  type CopilotFixSplitButtonItem,
} from '@github-ui/copilot-fix-controls/CopilotFixSplitButton'
import {announce} from '@github-ui/aria-live'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {ScopedCommands} from '@github-ui/ui-commands'
import {useUnifiedBatch} from '../../../stores/layout-selectors'
import {useManageBatchModelPickerModelsUrl} from '../../../hooks/use-manage-batch-model-picker-models-url'
import {useApplyUnifiedBatchFixWithCopilotMutation} from '../../../mutations/use-apply-unified-batch-fix-with-copilot-mutation'
import {useManageBatchTelemetry} from './manage-batch-telemetry'
import styles from './ManageBatchFixTab.module.css'

/**
 * The two CCA hand-off modes surfaced by the Fix with Copilot tab's primary
 * CTA. `commit` pushes a Copilot-authored commit directly to the PR branch;
 * `pull_request` opens a new PR with the changes. Mirrors `APPLY_MODE_ITEMS`
 * in `CopilotFixDialog.tsx`.
 */
export type ManageBatchFixApplyMode = 'commit' | 'pull_request'

export const APPLY_MODE_ITEMS: Array<CopilotFixSplitButtonItem<ManageBatchFixApplyMode>> = [
  {
    value: 'commit',
    label: 'Fix and commit',
    description: "Copilot will push a commit directly to this pull request's branch.",
  },
  {
    value: 'pull_request',
    label: 'Fix and open pull request',
    description: 'Copilot will create a new pull request with the changes.',
  },
]

const BASE_ERROR_MESSAGE = 'There was an error sending the batch to Copilot'
const FALLBACK_VIEWER_HANDLE = 'You'
const INSTRUCTIONS_CLEARED_MESSAGE = 'Additional instructions cleared for the new batch.'
const HANDOFF_SUCCESS_MESSAGE = 'Batch sent to Copilot cloud agent.'

type FormState = {
  additionalInstructions: string
  selectedModel: ModelOption | null
  applyMode: ManageBatchFixApplyMode
}

type FormAction =
  | {type: 'CHANGE_INSTRUCTIONS'; payload: string}
  | {type: 'CHANGE_MODEL'; payload: ModelOption}
  | {type: 'CHANGE_APPLY_MODE'; payload: ManageBatchFixApplyMode}

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'CHANGE_INSTRUCTIONS':
      return {...state, additionalInstructions: action.payload}
    case 'CHANGE_MODEL':
      return {...state, selectedModel: action.payload}
    case 'CHANGE_APPLY_MODE':
      return {...state, applyMode: action.payload}
  }
}

/**
 * Shared state between the Fix tab body (description + textarea + model picker)
 * and the footer primary CTA (`Fix and commit ▾` split button). Like the
 * Commit tab, these slots render in different places in `ManageBatchPanel`,
 * so the form values, submission status, and error message live in a context
 * provider that wraps both slots.
 */
type ManageBatchFixContextValue = {
  formValues: FormState
  setInstructions: (value: string) => void
  setSelectedModel: (modelId: string, model: ModelOption) => void
  setApplyMode: (mode: ManageBatchFixApplyMode) => void
  isSubmitting: boolean
  errorMessage: string | undefined
  submit: () => void
  canSubmit: boolean
  viewerHandle: string
  modelsUrl: string | undefined
  /** Set once the batch has been handed off; drives the "View agent session" link. */
  taskUrl: string | null
}

const ManageBatchFixContext = createContext<ManageBatchFixContextValue | undefined>(undefined)

function useManageBatchFixContext(): ManageBatchFixContextValue {
  const ctx = use(ManageBatchFixContext)
  if (!ctx) {
    throw new Error('ManageBatchFix components must be rendered inside <ManageBatchFixProvider>.')
  }
  return ctx
}

/**
 * Binds the `github:submit-form` command (⌘/Ctrl+Enter) to the Fix tab's
 * submit handler for its entire subtree, mirroring how `ApplySuggestionDialog`
 * wraps its form in `ScopedCommands` so the batch can be handed off to Copilot
 * via the keyboard while focus is anywhere in the form area.
 *
 * `ScopedCommands` renders a `display: contents` wrapper when no `className`
 * or `as` is supplied, so this does not affect the panel's flex layout.
 */
export const ManageBatchFixFormCommands = memo(function ManageBatchFixFormCommandsImpl({
  children,
}: {
  children: React.ReactNode
}) {
  const {submit} = useManageBatchFixContext()
  return <ScopedCommands commands={{'github:submit-form': submit}}>{children}</ScopedCommands>
})

/**
 * Provider for the Fix with Copilot tab.
 *
 * Wraps both the body slot (`ManageBatchFixTab`) and the footer primary CTA
 * slot (`ManageBatchFixTabPrimaryCta`) so they can share form state and the
 * submit callback without prop drilling through `ManageBatchPanel`.
 *
 * Hands the batch off to the Copilot cloud agent via
 * `useApplyUnifiedBatchFixWithCopilotMutation`, which reuses the per-comment
 * `useFixWithCopilot` submission with one `review_references` entry per batched
 * comment. On success the panel stays open and surfaces a "View agent session"
 * link built from the returned `taskUrl` — matching today's per-comment Fix with
 * Copilot dialog. `modelsUrl` is still optional (the route payload does not plumb
 * it through the panel); production discovers it from the pull-request layout
 * store, while tests and Storybook can override it through the provider prop.
 */
export const ManageBatchFixProvider = memo(function ManageBatchFixProviderImpl({
  children,
  viewerLogin,
  modelsUrl,
}: {
  children: React.ReactNode
  /**
   * Accepted for API symmetry with the Commit provider and the shared
   * `ActiveTabProvider`, but intentionally unused: on a successful Fix hand-off
   * the panel stays open to show the "View agent session" completion state, so
   * it does not auto-close.
   */
  onSubmitted?: () => void
  /**
   * Override for the templated handle. Tests and Storybook pass this to keep
   * snapshots deterministic; production reads it via `getCurrentUserLogin()`.
   */
  viewerLogin?: string
  /**
   * Override for the endpoint the `ModelPicker` fetches model options from.
   * Production discovers the endpoint from the pull-request layout store.
   */
  modelsUrl?: string
}) {
  const {items} = useUnifiedBatch()
  const discoveredModelsUrl = useManageBatchModelPickerModelsUrl()
  const effectiveModelsUrl = modelsUrl ?? discoveredModelsUrl
  const {onSubmit, onSubmitSuccess, onSubmitFailure} = useManageBatchTelemetry()
  const {mutateAsync, isPending: isSubmitting, taskUrl, resetTaskUrl} = useApplyUnifiedBatchFixWithCopilotMutation()
  const [formValues, dispatch] = useReducer(formReducer, {
    additionalInstructions: '',
    selectedModel: null,
    applyMode: 'commit',
  })
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [instructionsClearedCount, setInstructionsClearedCount] = useState(0)

  const setInstructions = useCallback((value: string) => dispatch({type: 'CHANGE_INSTRUCTIONS', payload: value}), [])
  const setSelectedModel = useCallback((_modelId: string, model: ModelOption) => {
    dispatch({type: 'CHANGE_MODEL', payload: model})
  }, [])
  const setApplyMode = useCallback(
    (value: ManageBatchFixApplyMode) => dispatch({type: 'CHANGE_APPLY_MODE', payload: value}),
    [],
  )

  const resolvedViewer = viewerLogin ?? getCurrentUserLogin() ?? FALLBACK_VIEWER_HANDLE

  // When a fresh batch starts, clear state that belongs to the previous batch.
  // Keep model and apply-mode selections as user preferences, but drop free-text
  // instructions, errors, and the completed task URL. Detect the empty → non-empty
  // edge via set-state-during-render (not an effect) so it can't misfire on the
  // success transition itself, where `onSuccess` clears the batch (items → 0) in
  // the same commit that sets `taskUrl`.
  const [prevItemCount, setPrevItemCount] = useState(items.length)
  if (prevItemCount !== items.length) {
    setPrevItemCount(items.length)
    if (prevItemCount === 0 && items.length > 0) {
      if (formValues.additionalInstructions) {
        dispatch({type: 'CHANGE_INSTRUCTIONS', payload: ''})
        setInstructionsClearedCount(count => count + 1)
      }
      if (errorMessage) {
        setErrorMessage(undefined)
      }
      if (taskUrl) {
        resetTaskUrl()
      }
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Batch additions occur outside this component; announce the observed transition.
    if (instructionsClearedCount > 0) {
      announce(INSTRUCTIONS_CLEARED_MESSAGE)
    }
  }, [instructionsClearedCount])

  // Unlike the Commit tab, the Fix tab is always valid as long as the batch
  // has items — there is no required input. The Fix path supports both
  // suggestion-bearing and human-comment-only items, which is what makes it
  // the fallback when the Commit tab's "all items must have suggestions"
  // precondition fails. Once handed off (`taskUrl` set), the CTA becomes the
  // "View agent session" link and re-submission is disabled.
  const canSubmit = items.length > 0 && !isSubmitting && !taskUrl

  const submit = useCallback(async () => {
    if (!canSubmit) return

    onSubmit('fix')
    setErrorMessage(undefined)

    try {
      await mutateAsync({
        createPullRequest: formValues.applyMode === 'pull_request',
        instructions: formValues.additionalInstructions.trim(),
        model: formValues.selectedModel?.id ?? 'auto',
      })

      // Keep telemetry on the awaited continuation so it still runs if the
      // panel unmounts while the hand-off request is pending.
      onSubmitSuccess('fix')
    } catch (error: unknown) {
      onSubmitFailure('fix', error)
      const friendly = error instanceof Error ? error.message : undefined
      setErrorMessage(friendly ? `${BASE_ERROR_MESSAGE}: ${friendly}` : `${BASE_ERROR_MESSAGE}.`)
    }
  }, [
    canSubmit,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    mutateAsync,
    formValues.applyMode,
    formValues.additionalInstructions,
    formValues.selectedModel,
  ])

  const value = useMemo<ManageBatchFixContextValue>(
    () => ({
      formValues,
      setInstructions,
      setSelectedModel,
      setApplyMode,
      isSubmitting,
      errorMessage,
      submit,
      canSubmit,
      viewerHandle: resolvedViewer,
      modelsUrl: effectiveModelsUrl,
      taskUrl,
    }),
    [
      formValues,
      setInstructions,
      setSelectedModel,
      setApplyMode,
      isSubmitting,
      errorMessage,
      submit,
      canSubmit,
      resolvedViewer,
      effectiveModelsUrl,
      taskUrl,
    ],
  )

  return <ManageBatchFixContext value={value}>{children}</ManageBatchFixContext>
})

/**
 * Body content for the Fix with Copilot tab.
 *
 * Renders the templated description sentence — *"Ask Copilot cloud agent to
 * address the comments in this batch. **{handle}** will be added as
 * co-author on any new commits."* — followed by the
 * `Add additional instructions for Copilot...` textarea with the `Auto ▾`
 * model picker pill inline in its toolbar.
 *
 * The textarea + toolbar container mirrors the styling lifted from
 * `CopilotFixDialog.module.css` (`.textareaContainer` / `.textareaToolbar`)
 * so the unified panel feels visually consistent with the per-comment Fix
 * with Copilot dialog.
 */
export const ManageBatchFixTab = memo(function ManageBatchFixTabImpl() {
  const {formValues, setInstructions, setSelectedModel, errorMessage, viewerHandle, modelsUrl, isSubmitting} =
    useManageBatchFixContext()

  return (
    <div className={styles.body} data-testid="manage-batch-fix-tab">
      <p className={styles.description}>
        Ask Copilot cloud agent to address the comments in this batch.{' '}
        <span className={styles.descriptionHandle} data-testid="manage-batch-fix-viewer-handle">
          {viewerHandle}
        </span>{' '}
        will be added as co-author on any new commits.
      </p>
      <div className={styles.textareaContainer}>
        <Textarea
          className={styles.instructionsTextarea}
          aria-label="Additional instructions for Copilot"
          placeholder="Add additional instructions for Copilot..."
          value={formValues.additionalInstructions}
          onChange={event => setInstructions(event.target.value)}
          rows={4}
          resize="none"
          readOnly={isSubmitting}
          data-testid="manage-batch-fix-instructions-input"
        />
        <div className={styles.textareaToolbar}>
          {modelsUrl ? (
            <ModelPicker
              modelsUrl={modelsUrl}
              selectedModel={formValues.selectedModel}
              onModelChange={setSelectedModel}
            />
          ) : (
            <Button
              variant="invisible"
              size="small"
              trailingVisual={TriangleDownIcon}
              disabled
              data-testid="manage-batch-fix-model-picker-fallback"
              aria-label="Model: Auto"
            >
              Auto
            </Button>
          )}
        </div>
      </div>
      {errorMessage && (
        // Wrapped in a `role="alert"` live region so screen readers announce
        // the submission error when it appears. `FormControl.Validation`
        // alone is not programmatically associated with any input here
        // (it is rendered outside a `FormControl`), so it would not be
        // announced without an explicit live region. See WCAG 4.1.3.
        <div role="alert">
          <FormControl.Validation variant="error" className={styles.errorRow} data-testid="manage-batch-fix-error">
            {errorMessage}
          </FormControl.Validation>
        </div>
      )}
    </div>
  )
})

/**
 * Primary CTA for the Fix with Copilot tab footer slot.
 *
 * Renders the green `Fix and commit ▾` split button; disabled when the batch
 * is empty or while a submission is in flight. The split caret reveals
 * `Fix and open pull request` as a secondary action. Lifts the
 * `CopilotFixSplitButton` + `APPLY_MODE_ITEMS` pattern from `CopilotFixDialog.tsx`.
 */
export const ManageBatchFixTabPrimaryCta = memo(function ManageBatchFixTabPrimaryCtaImpl() {
  const {
    submit,
    canSubmit,
    isSubmitting,
    taskUrl,
    formValues: {applyMode},
    setApplyMode,
  } = useManageBatchFixContext()

  // The completed hand-off replaces the focused split button. Focus and
  // announce the session link when it mounts so the result remains perceivable.
  const focusViewSessionLink = useCallback((node: HTMLElement | null) => {
    if (node) {
      node.focus()
      announce(HANDOFF_SUCCESS_MESSAGE)
    }
  }, [])

  if (taskUrl) {
    return (
      <Button
        as="a"
        href={taskUrl}
        ref={focusViewSessionLink}
        variant="primary"
        data-testid="manage-batch-fix-view-session"
      >
        View agent session
      </Button>
    )
  }

  return (
    <div data-testid="manage-batch-fix-cta">
      <CopilotFixSplitButton
        items={APPLY_MODE_ITEMS}
        selectedValue={applyMode}
        onSelectionChange={setApplyMode}
        onAction={submit}
        variant="primary"
        disabled={!canSubmit}
        loading={isSubmitting}
        dropdownAriaLabel="Change Fix with Copilot action"
      />
    </div>
  )
})
